import { type BetState, type RoundState } from '@urbanrats/contracts';
import { err, ok, type Result } from './result.ts';

export type TransitionError<S extends string> = {
  code: 'ILLEGAL_TRANSITION' | 'GUARD_FAILED';
  from: S;
  to: S;
  reason: string;
};

const ROUND_TRANSITIONS: Readonly<Record<RoundState, readonly RoundState[]>> = {
  SCHEDULED: ['BETTING_OPEN', 'VOIDED'],
  // Source failure before/while capturing: accepted stakes must be refunded (ADR-0003).
  BETTING_OPEN: ['BETTING_CLOSED', 'VOIDING'],
  BETTING_CLOSED: ['CAPTURING', 'VOIDING'],
  CAPTURING: ['VERIFYING', 'VOIDING'],
  // A rule-defined void (e.g. Next Class: different classes simultaneously) goes straight to VOIDING.
  VERIFYING: ['RESULT_FINAL', 'REVIEW_REQUIRED', 'VOIDING'],
  REVIEW_REQUIRED: ['RESULT_FINAL', 'VOIDING'],
  RESULT_FINAL: ['SETTLING'],
  SETTLING: ['SETTLED'],
  SETTLED: [],
  VOIDING: ['VOIDED'],
  VOIDED: [],
};

export interface RoundTransitionContext {
  /** Required for VOIDING -> VOIDED: all refund tasks resolved. */
  pendingRefunds?: number;
  /** Required for SETTLING -> SETTLED: all settlement tasks resolved. */
  pendingSettlements?: number;
  /** SCHEDULED -> VOIDED only when no bet was ever accepted. */
  acceptedBets?: number;
}

export function transitionRound(from: RoundState, to: RoundState, ctx: RoundTransitionContext = {}): Result<RoundState, TransitionError<RoundState>> {
  if (!ROUND_TRANSITIONS[from].includes(to)) return err({ code: 'ILLEGAL_TRANSITION', from, to, reason: 'not in round lifecycle' });
  if (from === 'VOIDING' && to === 'VOIDED' && (ctx.pendingRefunds ?? 1) !== 0)
    return err({ code: 'GUARD_FAILED', from, to, reason: 'refunds unresolved' });
  if (from === 'SETTLING' && to === 'SETTLED' && (ctx.pendingSettlements ?? 1) !== 0)
    return err({ code: 'GUARD_FAILED', from, to, reason: 'settlements unresolved' });
  if (from === 'SCHEDULED' && to === 'VOIDED' && (ctx.acceptedBets ?? 1) !== 0)
    return err({ code: 'GUARD_FAILED', from, to, reason: 'scheduled round cannot hold bets' });
  return ok(to);
}

const BET_TRANSITIONS: Readonly<Record<BetState, readonly BetState[]>> = {
  SUBMITTED: ['DEBIT_PENDING', 'REJECTED'],
  DEBIT_PENDING: ['ACCEPTED', 'REJECTED', 'COMPENSATION_PENDING'],
  ACCEPTED: ['RESULT_WON', 'RESULT_LOST', 'REFUND_PENDING'],
  REJECTED: [],
  // Debit confirmed after close: refunded regardless of outcome.
  COMPENSATION_PENDING: ['COMPENSATED'],
  COMPENSATED: [],
  RESULT_WON: ['CREDIT_PENDING'],
  // Loss is reported with a zero-amount win callback.
  RESULT_LOST: ['CREDIT_PENDING'],
  CREDIT_PENDING: ['SETTLED'],
  SETTLED: [],
  REFUND_PENDING: ['VOIDED'],
  VOIDED: [],
};

export const BET_TERMINAL_STATES: ReadonlySet<BetState> = new Set(['REJECTED', 'COMPENSATED', 'SETTLED', 'VOIDED']);
export const ROUND_TERMINAL_STATES: ReadonlySet<RoundState> = new Set(['SETTLED', 'VOIDED']);

export function transitionBet(from: BetState, to: BetState): Result<BetState, TransitionError<BetState>> {
  if (!BET_TRANSITIONS[from].includes(to)) return err({ code: 'ILLEGAL_TRANSITION', from, to, reason: 'not in bet lifecycle' });
  return ok(to);
}

/**
 * Financial finality, independent of what the UI is animating.
 * FINAL means no further money movement is expected for this bet.
 */
export type FinancialFinality = 'NONE' | 'PENDING' | 'FINAL';

export function financialFinality(state: BetState): FinancialFinality {
  switch (state) {
    case 'SUBMITTED':
      return 'NONE';
    case 'DEBIT_PENDING':
    case 'ACCEPTED':
    case 'RESULT_WON':
    case 'RESULT_LOST':
    case 'CREDIT_PENDING':
    case 'REFUND_PENDING':
    case 'COMPENSATION_PENDING':
      return 'PENDING';
    case 'REJECTED':
    case 'COMPENSATED':
    case 'SETTLED':
    case 'VOIDED':
      return 'FINAL';
  }
}
